import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { formatDay, formatDisplayDate, type MessageKey } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { formatMoneyForDisplay, parseMinorUnits } from '../money.js'

/**
 * Appendix Z's reports, all of them, on one screen. `RPT-01` to `RPT-11`.
 *
 * **One renderer, not twenty-one screens.** `GET /reports` returns every report this role may run
 * with its columns and its parameters, so the listing is the schema: a column's `kind` decides how
 * a cell is formatted and the parameter list decides what to ask for. Appendix Z has twenty-two
 * reports and twenty-one of them have endpoints; a screen each would be twenty-one things to keep
 * in step with one appendix, and the first time a column changed there the screen would be wrong
 * and nothing would say so.
 *
 * **`RPT-11` decides who sees what, and it has already decided by the time this renders.** The
 * class is a rule over the columns rather than a list (`decisions/0009` decision 4), the server
 * filters the listing by the caller's role, and the money columns of a financial report sit behind
 * `report_financial`. So this screen offers a report or does not; it never tries one and shows a
 * 403, and it never decides a permission itself.
 *
 * **Money is read, never recomputed.** Every `money` column is minor units plus the report's
 * currency, formatted by `../money.ts` like everywhere else. There is no total row: Appendix Z
 * states no totals, and a sum computed here would be a figure the server never produced and cannot
 * be reconciled against (`MR-04` spends its rounding on the write path).
 */

export interface ReportColumnData {
  readonly key: string
  readonly label: string
  /** `dimension`, `money`, `quantity`, `percent`, `days`, `date` or `unspecified`. */
  readonly kind: string
}

export interface ReportEntry {
  readonly id: string
  readonly title: string
  readonly reportClass: 'financial' | 'operational'
  /** The server saying it has implemented this one. Appendix Z has one it has not. */
  readonly available: boolean
  readonly columns: readonly ReportColumnData[]
  readonly parameters: readonly { readonly key: string; readonly defaultValue?: number }[]
}

export interface ReportListing {
  readonly reports: readonly ReportEntry[]
}

export interface ReportRun {
  readonly rows: readonly Record<string, unknown>[]
  /** Whatever the report sent beside its rows: `asOf`, the branch, the period. */
  readonly meta: Readonly<Record<string, unknown>>
  /** `ROLE-001a`: figures this caller may not see, in the server's own field names. */
  readonly withheld?: readonly string[]
}

export interface ReportsProps {
  readonly loadListing: () => Promise<ReportListing>
  readonly runReport: (
    reportId: string,
    parameters: Readonly<Record<string, string>>,
  ) => Promise<ReportRun>
  /** `BR-01`: supplied for the reports whose parameters include a branch. */
  readonly branchId: string
  readonly timezone: string
}

/**
 * The server's field names for what `ROLE-001a` can withhold, each against the words for it.
 *
 * **A map of literals rather than `t(`reports.withheldField.${name}`)`**, which is what this was.
 * The template worked and `i18n-static.test.ts` could not see it: that scan matches single-quoted
 * key literals, so all eight keys read as used by nothing while being used one line below their
 * own list. Its own comment names this failure shape, "a check that silently covers less as the
 * catalogue grows", and a key the scan cannot see is a key nobody can safely delete.
 *
 * Typing the record by the union also makes it exhaustive: a ninth field added to the union
 * without its words is a compile error rather than a raw `margin_pct` on a shopkeeper's screen.
 */
const WITHHELD_FIELDS = [
  'cogs',
  'cost_value',
  'due',
  'margin',
  'margin_pct',
  'net',
  'shop_cost',
  'supplier_credit',
] as const
type WithheldField = (typeof WITHHELD_FIELDS)[number]

const WITHHELD_WORDS: Readonly<Record<WithheldField, MessageKey>> = {
  cogs: 'reports.withheldField.cogs',
  cost_value: 'reports.withheldField.cost_value',
  due: 'reports.withheldField.due',
  margin: 'reports.withheldField.margin',
  margin_pct: 'reports.withheldField.margin_pct',
  net: 'reports.withheldField.net',
  shop_cost: 'reports.withheldField.shop_cost',
  supplier_credit: 'reports.withheldField.supplier_credit',
}

function isWithheldField(name: string): name is WithheldField {
  return (WITHHELD_FIELDS as readonly string[]).includes(name)
}

export function Reports({ loadListing, runReport, branchId, timezone }: ReportsProps): JSX.Element {
  const { t, locale } = useTranslation()
  const [listing, setListing] = useState<ReportListing | null>(null)
  const [chosen, setChosen] = useState<ReportEntry | null>(null)
  const [params, setParams] = useState<Record<string, string>>({})
  const [run, setRun] = useState<ReportRun | null>(null)
  // Three different things were sharing one string: a listing that would not load, a report that
  // would not run, and a parameter left blank. Each is its own state, or a failed run would show
  // under the listing and a blank date would look like a broken server.
  const [error, setError] = useState<string | null>(null)
  const [listingError, setListingError] = useState<string | null>(null)
  const [runError, setRunError] = useState<string | null>(null)
  const [running, setRunning] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setListing(await loadListing())
      setListingError(null)
    } catch {
      setListingError(t('reports.loadFailed'))
    }
  }, [loadListing, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  /**
   * The parameters a report declares, as fields. `branch` is filled from the shell rather than
   * asked for: §17.1 scopes a report to the branch in context and a second branch picker here
   * would let an owner run a report for a branch the rail does not say they are looking at.
   */
  const choose = useCallback(
    (entry: ReportEntry) => {
      setChosen(entry)
      setRun(null)
      setError(null)
      setRunError(null)
      const next: Record<string, string> = {}
      for (const parameter of entry.parameters) {
        if (parameter.key === 'branch') next['branchId'] = branchId
        else if (parameter.defaultValue !== undefined) {
          next[parameter.key] = String(parameter.defaultValue)
        } else next[parameter.key] = ''
      }
      setParams(next)
    },
    [branchId],
  )

  const execute = useCallback(async () => {
    if (!chosen) return
    const missing = Object.entries(params).filter(([, value]) => value.trim() === '')
    if (missing.length > 0) {
      setError(t('reports.parametersRequired', { count: missing.length }))
      return
    }
    setError(null)
    setRunError(null)
    setRun(null)
    setRunning(true)
    try {
      setRun(await runReport(chosen.id, params))
    } catch {
      setRunError(t('reports.runFailed'))
    } finally {
      setRunning(false)
    }
  }, [chosen, params, runReport, t])

  /**
   * One cell, by its column's kind.
   *
   * `money` is minor units plus the report's own currency, and the currency comes from the row
   * rather than from the branch: a report may span branches (`panel_statement` and
   * `supplier_ageing` both do) and `MR-01` makes an amount meaningless without it.
   */
  const cell = (row: Record<string, unknown>, column: ReportColumnData): string => {
    const raw = row[column.key]
    if (raw === null || raw === undefined) return t('reports.noValue')
    if (column.kind === 'money') {
      const money = raw as { minor?: string; currency?: string }
      if (typeof money?.minor !== 'string' || typeof money.currency !== 'string') {
        return String(raw)
      }
      return formatMoneyForDisplay(parseMinorUnits(money.minor), money.currency)
    }
    if (column.kind === 'date') {
      const instant = Date.parse(String(raw))
      return Number.isFinite(instant) ? formatDisplayDate(instant, timezone, locale) : String(raw)
    }
    if (column.kind === 'percent') {
      const ratio = raw as { milliPercent?: string }
      return typeof ratio?.milliPercent === 'string'
        ? `${(Number(ratio.milliPercent) / 1000).toFixed(2)}%`
        : String(raw)
    }
    return String(raw)
  }

  const numeric = (kind: string): boolean =>
    kind === 'money' || kind === 'quantity' || kind === 'percent' || kind === 'days'

  /**
   * `RPT-09`'s "as at", in words.
   *
   * The branch is left out: the shell already says which branch you are in, and the id it came as
   * is not something anybody can read. A key this build has no word for still prints as itself,
   * rather than vanishing, so a new report's own parameter is visible the day it is added.
   */
  const metaSentence = (key: string, value: unknown): string | null => {
    if (key === 'branchId' || key === 'branchIds') return null
    const text = String(value)
    if (key === 'asOf') return t('reports.asOf', { when: formatDay(text, timezone, locale) })
    if (key === 'from') return t('reports.from', { when: formatDay(text, timezone, locale) })
    if (key === 'to') return t('reports.to', { when: formatDay(text, timezone, locale) })
    if (key === 'period') return t('reports.period', { period: text })
    return `${key}: ${text}`
  }

  const metaLine =
    run === null
      ? null
      : Object.entries(run.meta)
          .map(([key, value]) => metaSentence(key, value))
          .filter((line): line is string => line !== null)
          .join(' · ') || null

  return (
    <section className="lm-screen" aria-labelledby="reports-heading">
      <h1 id="reports-heading" className="lm-screen__heading">
        {t('reports.heading')}
      </h1>

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      <DataSurface
        state={
          listingError !== null && listing === null
            ? {
                kind: 'error',
                message: listingError,
                onRetry: () => {
                  setListingError(null)
                  void refresh()
                },
              }
            : listing === null
              ? { kind: 'loading' }
              : listing.reports.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={4}
        what={t('reports.what')}
        emptyMessage={t('reports.listEmpty')}
      >
        <>
          {/* Grouped by class, because `RPT-11` makes the class the permission and an owner reading
              a financial figure is doing something different from a dispenser chasing a job. */}
          {(['operational', 'financial'] as const).map((group) => {
            const inGroup = (listing?.reports ?? []).filter((r) => r.reportClass === group)
            if (inGroup.length === 0) return null
            return (
              <div key={group}>
                <h2 className="lm-screen__subheading">
                  {group === 'financial' ? t('reports.financial') : t('reports.operational')}
                </h2>
                <ul className="lm-report-list">
                  {inGroup.map((entry) => (
                    <li key={entry.id} className="lm-report-item">
                      <button
                        type="button"
                        className="lm-button lm-button--secondary"
                        disabled={!entry.available}
                        onClick={() => {
                          choose(entry)
                        }}
                      >
                        {entry.title}
                      </button>
                      {entry.available ? null : (
                        // Appendix Z lists it and the server has not built it. Said, rather than
                        // hidden, so an owner looking for it knows it is coming and not missing.
                        <span className="lm-report-item__pending">{t('reports.notBuiltYet')}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </>
      </DataSurface>

      {chosen === null ? null : (
        <div className="lm-form">
          <h2 className="lm-screen__subheading">{chosen.title}</h2>

          {Object.keys(params).filter((key) => key !== 'branchId').length === 0 ? null : (
            <>
              {Object.keys(params)
                .filter((key) => key !== 'branchId')
                .map((key) => (
                  <label key={key} className="lm-field lm-field--inline">
                    <span className="lm-field__label">
                      {/* The parameter's own key, which is Appendix Z's word for it. Not
                          translated: these are field names rather than sentences, and inventing a
                          shopkeeper's phrase for "threshold_days" would be guessing. */}
                      <Ltr>{key}</Ltr>
                    </span>
                    <input
                      className="lm-input"
                      type={key === 'from' || key === 'to' || key === 'date' ? 'date' : 'text'}
                      value={params[key] ?? ''}
                      onChange={(e) => {
                        // Captured before the updater runs. `e.currentTarget` is only valid while
                        // the event is dispatching: React nulls it afterwards, and a functional
                        // `setState` updater runs later, so reading it in there throws during the
                        // next render and unmounts the tree. The symptom is a missing button, not
                        // an error, which is how this took three logs to find.
                        const value = e.currentTarget.value
                        setParams((previous) => ({ ...previous, [key]: value }))
                      }}
                    />
                  </label>
                ))}
            </>
          )}

          <button
            type="button"
            className="lm-button lm-button--primary"
            onClick={() => void execute()}
          >
            {t('reports.run')}
          </button>
        </div>
      )}

      {/*
        The parameter form above stays out of the surface, so a failed or empty run can be asked
        again with different dates. Nothing is drawn before the first Run.

        Empty or filtered is decided by whether the report took a parameter beyond the branch, not
        by the row count: a date range that matched nothing is a different sentence from a shop
        that has not recorded anything yet. Partial is `ROLE-001a`: the rows are there and some
        figures on them are not this person's to see.
      */}
      {chosen === null || (!running && runError === null && run === null) ? null : (
        <>
          {run === null || running || runError !== null || metaLine === null ? null : (
            <p className="lm-hint">{metaLine}</p>
          )}
          <DataSurface
            state={
              running
                ? { kind: 'loading' }
                : runError !== null
                  ? {
                      kind: 'error',
                      message: runError,
                      onRetry: () => {
                        void execute()
                      },
                    }
                  : run === null
                    ? { kind: 'ready' }
                    : run.rows.length === 0
                      ? chosen.parameters.some((parameter) => parameter.key !== 'branch')
                        ? { kind: 'filtered' }
                        : { kind: 'empty' }
                      : run.withheld !== undefined && run.withheld.length > 0
                        ? {
                            kind: 'partial',
                            missing: run.withheld
                              .map((name) =>
                                isWithheldField(name) ? t(WITHHELD_WORDS[name]) : name,
                              )
                              .join(', '),
                          }
                        : { kind: 'ready' }
            }
            shape="table"
            rows={5}
            columns={Math.min(chosen.columns.length, 6)}
            what={t('reports.whatRun')}
            emptyMessage={t('reports.runEmpty')}
            filteredMessage={t('reports.noRows')}
          >
            <table className="lm-line-table">
              <caption className="lm-rx-grid__caption">
                {t('reports.rowCount', { count: run?.rows.length ?? 0 })}
              </caption>
              <thead>
                <tr>
                  {chosen.columns.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      className={numeric(column.kind) ? 'lm-line-table__num' : undefined}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(run?.rows ?? []).map((row, index) => (
                  <tr key={index}>
                    {chosen.columns.map((column) => (
                      <td
                        key={column.key}
                        className={numeric(column.kind) ? 'lm-line-table__num' : undefined}
                        {...(numeric(column.kind) ? { dir: 'ltr' as const } : {})}
                      >
                        {cell(row, column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </DataSurface>
        </>
      )}
    </section>
  )
}
